import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useOrganization } from '../hooks/useOrganization';
import { createInvitation, getOrganizationInvitations, sendInvitationEmail } from '../utils/invitations';
import { Building, Users, UserPlus, Mail, Crown, User, Trash2, AlertCircle, Check, Clock, X, Edit2 } from 'lucide-react';
import type { Invitation, UserProfile, UserRole } from '../types';
import LiquidGlass from './LiquidGlass';

export default function Organization() {
  const { user } = useAuth();
  const { organization, userProfile, isAdmin, canInviteUsers, refetch } = useOrganization();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [organizationUsers, setOrganizationUsers] = useState<UserProfile[]>([]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [companyName, setCompanyName] = useState<string>('');
  const [editingCompanyName, setEditingCompanyName] = useState(false);
  const [inviteData, setInviteData] = useState({
    email: '',
    role: 'Member' as UserRole,
    firstName: '' as string | null,
    lastName: '' as string | null,
    companyName: null as string | null,
    isExistingUser: false
  });

  useEffect(() => {
    if (organization && isAdmin) {
      loadInvitations();
      loadOrganizationUsers();
    }
  }, [organization, isAdmin]);

  const loadInvitations = async () => {
    if (!organization) return;

    try {
      const data = await getOrganizationInvitations(organization.id);
      setInvitations(data);
    } catch (err: any) {
      console.error('Error loading invitations:', err);
    }
  };

  const loadOrganizationUsers = async () => {
    if (!organization) return;

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('organization_id', organization.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setOrganizationUsers(data || []);
      
      // Set company name if available from any organization user
      if (data && data.length > 0) {
        const firstUserWithCompany = data.find(user => user.company_name);
        if (firstUserWithCompany && firstUserWithCompany.company_name) {
          setCompanyName(firstUserWithCompany.company_name);
        }
      }
    } catch (err: any) {
      console.error('Error loading organization users:', err);
    }
  };

  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organization) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Create invitation and get invitation details
      const result = await createInvitation({
        email: inviteData.email,
        organizationId: organization.id,
        role: inviteData.role, 
        firstName: inviteData.firstName?.trim() || null,
        lastName: inviteData.lastName?.trim() || null,
        companyName: inviteData.companyName || null
      });

      console.log("Invitation created successfully:", result);

      // Send invitation email - Fix: properly access array result
      await sendInvitationEmail({
        invitationId: Array.isArray(result) ? result[0].invitation_id : result.invitation_id,
        token: Array.isArray(result) ? result[0].token : result.token,
        organizationName: organization.name,
        adminName: userProfile?.first_name 
          ? `${userProfile.first_name} ${userProfile.last_name || ''}`.trim()
          : undefined,
        recipientEmail: inviteData.email
      });

      setSuccess('Invitation sent successfully!');
      setInviteData({
        email: '',
        role: 'Member',
        firstName: '',
        lastName: '',
        companyName: null,
        isExistingUser: false
      });
      setShowInviteForm(false);
      await loadInvitations();
      await refetch();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to remove this user from the organization?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('user_profiles')
        .delete()
        .eq('user_id', userId)
        .eq('organization_id', organization?.id);

      if (error) throw error;

      setSuccess('User removed successfully');
      await loadOrganizationUsers();
      await refetch();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUpdateUserRole = async (userId: string, newRole: UserRole) => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ role: newRole })
        .eq('user_id', userId)
        .eq('organization_id', organization?.id);

      if (error) throw error;

      setSuccess('User role updated successfully');
      await loadOrganizationUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUpdateCompanyName = async () => {
    if (!organization) return;
    setEditingCompanyName(false);
    
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ company_name: companyName })
        .eq('organization_id', organization.id);

      if (error) throw error;

      setSuccess('Company name updated successfully');
      // No need to update local state again as it's already set
      await loadOrganizationUsers();
      await refetch();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      const { error } = await supabase
        .from('invitations')
        .update({ status: 'expired' })
        .eq('id', invitationId);

      if (error) throw error;

      setSuccess('Invitation cancelled successfully');
      await loadInvitations();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Check if email exists in auth.users
  const checkExistingUser = async (email: string) => {
    try {
      // Using the RPC function to check for existing users
      const { data, error } = await supabase.rpc('check_user_exists', {
        p_email: email
      });
      
      if (error) throw error;
      
      setInviteData(prev => ({
        ...prev,
        isExistingUser: !!data
      }));
      
      return !!data;
    } catch (err) {
      console.error('Error checking existing user:', err);
      return false;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!user || !userProfile) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 w-48 bg-gray-200 animate-pulse rounded"></div>
          <div className="h-12 w-36 bg-gray-200 animate-pulse rounded-md"></div>
        </div>
        
        {/* Organization Overview Skeleton */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-6 w-6 bg-gray-200 animate-pulse rounded-full"></div>
            <div>
              <div className="h-6 w-48 bg-gray-200 animate-pulse rounded mb-1"></div>
              <div className="h-4 w-32 bg-gray-200 animate-pulse rounded"></div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="h-5 w-5 bg-gray-200 animate-pulse rounded-full"></div>
                  <div>
                    <div className="h-4 w-24 bg-gray-200 animate-pulse rounded mb-1"></div>
                    <div className="h-8 w-16 bg-gray-200 animate-pulse rounded"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No organization found. Please contact support.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800">Organization</h1>
        <div className="flex gap-2">
          {isAdmin && canInviteUsers && (
            <LiquidGlass variant="black-button" hover glowColor="black" bgOpacity="high">
              <button
                onClick={() => setShowInviteForm(true)}
                className="flex items-center gap-2 px-6 py-3 text-white font-medium"
              >
                <UserPlus className="w-4 h-4" />
                Invite User
              </button>
            </LiquidGlass>
          )}
        </div>
      </div>

      {error && (
        <LiquidGlass className="bg-red-100/60 border-red-200/60 p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        </LiquidGlass>
      )}

      {success && (
        <LiquidGlass className="bg-green-100/60 border-green-200/60 p-4">
          <div className="flex items-center gap-2 text-green-700">
            <Check className="w-5 h-5" />
            {success}
          </div>
        </LiquidGlass>
      )}

      {/* Organization Overview */}
      <LiquidGlass variant="card" className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Building className="w-6 h-6 text-gray-700" />
          <div>
            <h2 className="text-xl font-semibold text-gray-800">{organization.name}</h2>
            <p className="text-sm text-gray-500">Organization Details</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gray-50/80 p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-sm text-gray-600">Active Users</p>
                <p className="text-2xl font-bold text-gray-800">
                  {organization.active_users_count}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50/80 p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <Crown className="w-5 h-5 text-purple-600" />
              <div>
                <p className="text-sm text-gray-600">Total Seats</p>
                <p className="text-2xl font-bold text-gray-800">
                  {organization.total_seats}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50/80 p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <UserPlus className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">Available Seats</p>
                <p className="text-2xl font-bold text-gray-800">
                  {organization.total_seats - organization.active_users_count}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Company Name section removed as requested */}
      </LiquidGlass>

      {/* Invite User Modal */}
      {showInviteForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <LiquidGlass variant="modal" className="w-full max-w-md p-6" bgOpacity="ultra">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-800">Invite User</h3>
              <button
                onClick={() => setShowInviteForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleInviteUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Email Address</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    value={inviteData.email}
                    onChange={(e) => {
                      setInviteData({ ...inviteData, email: e.target.value });
                      if (e.target.value.includes('@')) {
                        checkExistingUser(e.target.value);
                      }
                    }}
                    className="block w-full pl-10 pr-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 focus:border-blue-300 focus:ring-2 focus:ring-blue-200"
                    placeholder="colleague@example.com"
                    required
                  />
                </div>
                {inviteData.isExistingUser && (
                  <p className="mt-2 text-sm text-blue-600 flex items-center gap-1">
                    <Check className="w-4 h-4" />
                    Existing user detected! They'll be able to join with their existing account.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  value={inviteData.role}
                  onChange={(e) => setInviteData({ ...inviteData, role: e.target.value as UserRole })}
                  className="mt-1 block w-full px-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 focus:border-blue-300 focus:ring-2 focus:ring-blue-200"
                >
                  <option value="Member">Member</option>
                  <option value="Admin">Admin</option>
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  {inviteData.role === 'Admin' ? 
                    'Admins can manage users and organization settings' : 
                    'Members can access organization events and results'}
                </p>
              </div>

              {!inviteData.isExistingUser && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">First Name</label>
                    <input
                      type="text"
                      value={inviteData.firstName || ''}
                      onChange={(e) => setInviteData({ ...inviteData, firstName: e.target.value || null })}
                      className="mt-1 block w-full px-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 focus:border-blue-300 focus:ring-2 focus:ring-blue-200"
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Name</label>
                    <input
                      type="text"
                      value={inviteData.lastName || ''}
                      onChange={(e) => setInviteData({ ...inviteData, lastName: e.target.value || null })}
                      className="mt-1 block w-full px-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 focus:border-blue-300 focus:ring-2 focus:ring-blue-200"
                      placeholder="Optional"
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowInviteForm(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <LiquidGlass variant="black-button" hover glowColor="black" bgOpacity="high">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 text-white font-medium disabled:opacity-50"
                  >
                    <Mail className="w-4 h-4" />
                    {loading ? 'Sending...' : 'Send Invitation'}
                  </button>
                </LiquidGlass>
              </div>
            </form>
          </LiquidGlass>
        </div>
      )}

      {isAdmin && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Organization Users */}
          <LiquidGlass variant="card" className="p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Organization Members</h3>
            <div className="space-y-3">
              {organizationUsers.map((orgUser) => (
                <div key={orgUser.id} className="flex items-center justify-between p-3 bg-gray-50/80 rounded-lg">
                  <div className="flex items-center gap-3">
                    {orgUser.profile_photo_url ? (
                      <img
                        src={orgUser.profile_photo_url}
                        alt="Profile"
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-gray-600" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-gray-800">
                        {orgUser.first_name && orgUser.last_name
                          ? `${orgUser.first_name} ${orgUser.last_name}`
                          : 'No Name Provided'}
                      </p>
                      <p className="text-sm text-gray-600">{orgUser.role}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {orgUser.user_id !== user.id && (
                      <>
                        <select
                          value={orgUser.role}
                          onChange={(e) => handleUpdateUserRole(orgUser.user_id, e.target.value as UserRole)}
                          className="text-sm px-2 py-1 bg-white border border-gray-200 rounded"
                        >
                          <option value="Member">Member</option>
                          <option value="Admin">Admin</option>
                        </select>
                        <button
                          onClick={() => handleDeleteUser(orgUser.user_id)}
                          className="text-red-600 hover:text-red-700 p-1"
                          aria-label="Remove user"
                          title="Remove user from organization"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {orgUser.user_id === user.id && (
                      <span className="text-sm text-gray-500">(You)</span>
                    )}
                  </div>
                </div>
              ))}
              
              {organizationUsers.length === 0 && (
                <div className="text-center py-6 text-gray-500">
                  No members found
                </div>
              )}
            </div>
          </LiquidGlass>

          {/* Pending Invitations */}
          <LiquidGlass variant="card" className="p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Pending Invitations</h3>
            <div className="space-y-3">
              {invitations.filter(inv => inv.status === 'pending').map((invitation) => (
                <div key={invitation.id} className="flex items-center justify-between p-3 bg-gray-50/80 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-yellow-600" />
                    <div>
                      <p className="font-medium text-gray-800">{invitation.email}</p>
                      <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                        <span>{invitation.role}</span>
                        <span>•</span>
                        <span>Expires {formatDate(invitation.expires_at)}</span>
                        {(invitation.first_name || invitation.last_name) && (
                          <p className="text-sm text-gray-600 w-full mt-1">
                            Name: {[invitation.first_name, invitation.last_name].filter(Boolean).join(' ')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(invitation.status)}`}>
                      {invitation.status}
                    </span>
                    <button
                      onClick={() => handleCancelInvitation(invitation.id)}
                      className="text-red-600 hover:text-red-700 p-1"
                      aria-label="Cancel invitation"
                      title="Cancel invitation"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {invitations.filter(inv => inv.status === 'pending').length === 0 && (
                <p className="text-gray-500 text-center py-4">No pending invitations</p>
              )}
              
              {invitations.some(inv => inv.status === 'accepted') && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <h4 className="text-base font-medium text-gray-700 mb-3">Recent Accepted Invitations</h4>
                  <div className="space-y-2">
                    {invitations
                      .filter(inv => inv.status === 'accepted')
                      .slice(0, 3)
                      .map((invitation) => (
                        <div key={invitation.id} className="flex items-center justify-between p-2 bg-green-50/80 rounded">
                          <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-600" />
                            <p className="text-sm text-green-800">{invitation.email}</p>
                          </div>
                          <span className="text-xs text-green-700">
                            {new Date(invitation.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </LiquidGlass>
        </div>
      )}

      {!isAdmin && (
        <LiquidGlass variant="card" className="p-6">
          <div className="text-center py-8">
            <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Organization Member</h3>
            <p className="text-gray-600">
              You are a member of {organization.name}. Contact an administrator to manage organization settings.
            </p>
          </div>
        </LiquidGlass>
      )}
    </div>
  );
}